import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { TaskNavigationSectionSchema } from "@nexus/contracts";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import {
  adPlatformsQueryOptions,
  pageStateQueryOptions,
  PageAccessError,
  personaOpportunitySetsQueryOptions,
  personaOpportunitySetDetailQueryOptions,
  personaOpportunitySetKeys,
  normalizedPersonaOpportunitySetTenantId,
  useGeneratePersonaOpportunitySet,
  useDeletePersonaOpportunitySet,
  useRetryPersonaOpportunitySet,
  useRegeneratePersonaOpportunitySet,
  activeTasksQueryOptions,
  PERSONA_SCRIPTS_LIVE_REFETCH_MS,
} from "@/lib/queries";
import { ApiError } from "@/lib/api-client";
import { PageError } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PersonaOpportunitiesPage } from "@/features/persona-opportunities/components/page/PersonaOpportunitiesPage/PersonaOpportunitiesPage";
import { PersonaOpportunitiesLoadingState } from "@/features/persona-opportunities/components/page/PersonaOpportunitiesLoadingState/PersonaOpportunitiesLoadingState";
import { usePersonaOpportunityPermissions } from "@/features/persona-opportunities/hooks/usePersonaOpportunityPermissions";
import { useInvalidatePersonaSetsOnGenerationTaskEnd } from "@/features/persona-opportunities/hooks/useInvalidatePersonaSetsOnGenerationTaskEnd";
import { usePersonaSetGenerationToasts } from "@/features/persona-opportunities/hooks/usePersonaSetGenerationToasts";
import { buildGeneratePersonaOpportunitySetRequest } from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import type {
  PersonaOpportunitySetDetail,
  PersonaOpportunitySetListPage,
} from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import {
  toPersonaOpportunitySet,
  toPersonaOpportunitySetFromDetail,
} from "@/features/persona-opportunities/lib/persona-opportunity-set-mapper";
import { isPersonaScriptsGenerationActiveForSet } from "@/features/persona-opportunities/lib/persona-scripts-generation-active";
import {
  PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES,
  PERSONA_SCRIPTS_LIST_SEARCH_KEYS,
  PERSONA_SCRIPTS_SENT_STATUS_VALUES,
  PERSONA_SCRIPTS_YES_FILTER_VALUES,
} from "@/features/persona-opportunities/lib/persona-scripts-list-search";
import {
  applyOptimisticCreateToList,
  createOptimisticGeneratingSetRow,
  createOptimisticPersonaSetRow,
  createPendingPersonaSetId,
  isPendingPersonaSetId,
  markPersonaSetGeneratingInDetail,
  markPersonaSetGeneratingInList,
  removePersonaSetFromList,
  replacePersonaSetIdInList,
} from "@/features/persona-opportunities/lib/persona-opportunity-set-generation-state";
import { clearPersonaOpportunitySetSuppressions } from "@/features/persona-opportunities/lib/persona-opportunity-set-suppression";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useStopPersonaOpportunityGeneration } from "@/features/task-progress/hooks/useStopPersonaOpportunityGeneration";
import { useTaskSectionJump } from "@/features/task-progress/hooks/useTaskSectionJump";
import type {
  CreatePersonaSetFormValues,
  PersonaOpportunitySet,
  PersonaOpportunityStatusFilter,
  PersonaOpportunityTabKey,
  PersonaOpportunityViewMode,
} from "@/features/persona-opportunities/types/persona-opportunities.types";

const TAB_VALUES: ["opportunities", "scripts", "sent_to_vendor"] = [
  "opportunities",
  "scripts",
  "sent_to_vendor",
];

const VIEW_VALUES: ["cards", "table"] = ["cards", "table"];

const STATUS_FILTER_VALUES: [
  "all",
  "NEW",
  "SCRIPTS_GENERATED",
  "BRIEFS_GENERATED",
  "SCRIPTS_AND_BRIEFS_GENERATED",
  "SENT_TO_VENDOR",
] = [
  "all",
  "NEW",
  "SCRIPTS_GENERATED",
  "BRIEFS_GENERATED",
  "SCRIPTS_AND_BRIEFS_GENERATED",
  "SENT_TO_VENDOR",
];

/** Query booleans arrive as strings; `z.coerce.boolean()` treats `"false"` as truthy. */
const booleanSearchParam = z
  .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
  .optional();

/** Digit-only search is decoded as a number; keep it as the typed query string. */
const stringSearchParam = z.preprocess(
  (value) => (typeof value === "number" && Number.isFinite(value) ? String(value) : value),
  z.string().optional(),
).catch(undefined);

const personaOpportunitiesSearchSchema = z.object({
  setId: z.string().optional().catch(undefined),
  tab: z.enum(TAB_VALUES).catch("opportunities"),
  view: z.enum(VIEW_VALUES).catch("table"),
  q: z.string().optional().catch(undefined),
  status: z.enum(STATUS_FILTER_VALUES).catch("all"),
  /**
   * Scripts-tab list lenses. Dedicated names so they never collide with angles
   * `q` / `status` or the storyboard deep-link `variant`. Keys come from
   * PERSONA_SCRIPTS_LIST_SEARCH_KEYS so read/write/validation stay aligned.
   */
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q]: stringSearchParam,
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind]: z
    .enum(PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent]: z
    .enum(PERSONA_SCRIPTS_SENT_STATUS_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch]: z
    .enum(PERSONA_SCRIPTS_YES_FILTER_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic]: z
    .enum(PERSONA_SCRIPTS_YES_FILTER_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites]: booleanSearchParam.catch(undefined),
  createSet: booleanSearchParam.catch(undefined),
  /**
   * A generation started on another page (the persona library's create-set flow),
   * handed over as its optimistic row id plus correlation id so this route can
   * adopt the run. Consumed and cleared on arrival.
   */
  pendingSetId: z.string().optional().catch(undefined),
  pendingCorrelationId: z.string().optional().catch(undefined),
  /** Script id whose animatic storyboard modal is open — shareable link state. */
  storyboard: z.string().optional().catch(undefined),
  /** Deep-link: auto-expand this script's group and row. */
  scriptId: z.string().optional().catch(undefined),
  /** Deep-link: auto-expand this brief's group and card. */
  briefId: z.string().optional().catch(undefined),
  /** Angle group to expand when a generate-scripts card has no scriptId. */
  opportunityId: z.string().optional().catch(undefined),
  /**
   * One-shot deep-link seed from a completed storyboard task card — selects the
   * Sketch/Animatic toggle. Cleared after the storyboard view applies it; not
   * persistent UI state.
   */
  variant: z.enum(["sketch", "panels"]).optional().catch(undefined),
  /** Precise scroll target from `TaskNavigation.itemIds` — see `TASK_ITEM_SEARCH_PARAM`. */
  highlight: z.string().optional().catch(undefined),
  /** Region a completed task card asked to jump to — see `TASK_NAVIGATION_SECTION`. */
  section: TaskNavigationSectionSchema.optional().catch(undefined),
});

/** Exported for unit tests of deep-link search params. */
export { personaOpportunitiesSearchSchema };

type PersonaOpportunitiesSearch = z.infer<typeof personaOpportunitiesSearchSchema>;

const defaultSearch: PersonaOpportunitiesSearch = {
  tab: "opportunities",
  view: "table",
  status: "all",
};

interface PendingGeneration {
  correlationId: string;
  /** Optimistic list row standing in until the worker publishes the real set id. */
  placeholderId: string;
  /** Open the workspace once that real id arrives. */
  autoOpen: boolean;
  /**
   * The run's task record has been seen in at least one poll. Until then its
   * absence means "not discovered yet", not "finished".
   */
  taskSeen: boolean;
}

function PersonaOpportunitiesRoute() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const userEmail = useAuth((s) => s.user?.email);
  const { isLoading, error } = usePageState("persona-opportunities");
  const { canListSets, canCreateSet, canUseQueryTuning } = usePersonaOpportunityPermissions();
  const stopPersonaOpportunityGeneration = useStopPersonaOpportunityGeneration();
  useTaskSectionJump();

  useDocumentTitle(t("page.title.personaOpportunities"));

  // One entry per generation whose optimistic card is still waiting for the
  // worker's real set id. A tenant may run several at once, so the placeholder
  // and the "open it when it lands" intent belong to a correlation id rather
  // than to the page — two concurrent runs would otherwise overwrite each
  // other's bookkeeping and strand the loser's card forever.
  //
  // Retry and regenerate-overwrite reuse an existing set id, so they have
  // nothing to reconcile and are never tracked here.
  const [pendingGenerations, setPendingGenerations] = React.useState<
    readonly PendingGeneration[]
  >([]);

  const {
    data: listPage,
    isLoading: setsLoading,
    isError: setsError,
  } = useQuery(
    personaOpportunitySetsQueryOptions(activeTenantId, {
      enabled: canListSets,
    }),
  );

  const sets = React.useMemo<PersonaOpportunitySet[]>(
    () => (listPage?.items ?? []).map(toPersonaOpportunitySet),
    [listPage],
  );

  const { data: activeTasks = [] } = useQuery({
    ...activeTasksQueryOptions(userEmail ?? undefined),
    enabled: Boolean(userEmail),
  });

  useInvalidatePersonaSetsOnGenerationTaskEnd(activeTasks, activeTenantId);

  const isScriptsGenerating = isPersonaScriptsGenerationActiveForSet(activeTasks, search.setId);

  const { data: detail, error: detailError, isLoading: isDetailLoading, isFetching: isDetailFetching } = useQuery(
    personaOpportunitySetDetailQueryOptions(activeTenantId, search.setId, {
      enabled: canListSets,
      pollMs: isScriptsGenerating ? PERSONA_SCRIPTS_LIVE_REFETCH_MS : false,
    }),
  );

  // The workspace paints from the list row while the detail request is in flight;
  // the detail response then fills in the tab counts.
  const selectedSet = React.useMemo<PersonaOpportunitySet | null>(() => {
    if (!search.setId) return null;
    if (detail?.set.id === search.setId) return toPersonaOpportunitySetFromDetail(detail);
    return sets.find((set) => set.id === search.setId) ?? null;
  }, [detail, sets, search.setId]);

  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;

  const openSet = React.useCallback((setId: string, options?: { replace?: boolean }) => {
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        setId,
        tab: "opportunities",
        createSet: undefined,
      }),
      replace: options?.replace,
    });
  }, []);

  // Drop a `setId` the tenant cannot open — a stale bookmark, a deleted set, or an
  // id belonging to another tenant. Only a 404 prunes: a 5xx or a timeout should
  // leave the workspace in place and retry.
  React.useEffect(() => {
    if (!search.setId) return;
    if (!(detailError instanceof ApiError) || detailError.status !== 404) return;
    void navigateRef.current({
      search: (previous) => ({ ...previous, setId: undefined }),
      replace: true,
    });
  }, [detailError, search.setId]);

  // Switching tenants invalidates the visible set: the caches are tenant-keyed, so
  // only the URL needs clearing.
  const previousTenantIdRef = React.useRef(activeTenantId);
  React.useEffect(() => {
    if (previousTenantIdRef.current === activeTenantId) return;
    previousTenantIdRef.current = activeTenantId;
    setPendingGenerations([]);
    clearPersonaOpportunitySetSuppressions();

    if (search.setId) {
      void navigateRef.current({
        search: (previous) => ({ ...previous, setId: undefined }),
        replace: true,
      });
    }
  }, [activeTenantId, search.setId]);

  // A suppression only makes sense for the page instance that created it.
  React.useEffect(() => clearPersonaOpportunitySetSuppressions, []);

  /**
   * Registered from the mutation's own `onSuccess`, where the placeholder that
   * this correlation id belongs to is still in scope.
   */
  const trackPendingGeneration = React.useCallback(
    (correlationId: string, placeholderId: string, autoOpen: boolean) => {
      setPendingGenerations((prev) => [
        ...prev,
        { correlationId, placeholderId, autoOpen, taskSeen: false },
      ]);
    },
    [],
  );

  // A run started from the persona library arrives already in flight: its optimistic
  // row is in the list cache and its correlation id is in the URL. Adopting it here
  // is what lets the workspace open when the worker publishes the real set id, and
  // what clears the placeholder if the run ends without ever publishing one.
  const { pendingSetId, pendingCorrelationId } = search;
  const didAdoptHandedOverGenerationRef = React.useRef(false);
  React.useEffect(() => {
    if (didAdoptHandedOverGenerationRef.current) return;
    if (!pendingSetId || !pendingCorrelationId) return;
    didAdoptHandedOverGenerationRef.current = true;
    trackPendingGeneration(pendingCorrelationId, pendingSetId, true);
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        pendingSetId: undefined,
        pendingCorrelationId: undefined,
      }),
      replace: true,
    });
  }, [pendingSetId, pendingCorrelationId, trackPendingGeneration]);

  const untrackPendingGeneration = React.useCallback((correlationId: string) => {
    setPendingGenerations((prev) =>
      prev.filter((generation) => generation.correlationId !== correlationId),
    );
  }, []);

  const listQueryKey = React.useMemo(
    () => personaOpportunitySetKeys.list(normalizedPersonaOpportunitySetTenantId(activeTenantId)),
    [activeTenantId],
  );

  const removeOptimisticSet = React.useCallback(
    (setId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => removePersonaSetFromList(prev, setId),
      );
    },
    [activeTenantId, listQueryKey, queryClient],
  );

  /** Card/workspace loading UI keys off cached GENERATING — flip before the list poll. */
  const markSetGeneratingOptimistically = React.useCallback(
    (setId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => markPersonaSetGeneratingInList(prev, setId),
      );
      queryClient.setQueryData<PersonaOpportunitySetDetail>(
        personaOpportunitySetKeys.detail(
          normalizedPersonaOpportunitySetTenantId(activeTenantId),
          setId,
        ),
        (prev) => markPersonaSetGeneratingInDetail(prev, setId),
      );
    },
    [activeTenantId, listQueryKey, queryClient],
  );

  const bindRealSetId = React.useCallback(
    (generation: PendingGeneration, realSetId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => replacePersonaSetIdInList(prev, generation.placeholderId, realSetId),
      );
      // Also follow the swap when the user is sitting on the placeholder's
      // workspace, or the URL would point at an id that no longer exists.
      if (generation.autoOpen || search.setId === generation.placeholderId) {
        openSet(realSetId, { replace: true });
      }
    },
    [activeTenantId, listQueryKey, openSet, queryClient, search.setId],
  );

  const returnToSetsOverview = React.useCallback(() => {
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        setId: undefined,
        tab: defaultSearch.tab,
        view: defaultSearch.view,
      }),
      replace: true,
    });
  }, []);

  // Bind each run's real set id onto its own optimistic card as the task record
  // publishes it. Runs resolve independently and in any order.
  React.useEffect(() => {
    if (pendingGenerations.length === 0) return;

    const resolved = pendingGenerations.flatMap((generation) => {
      const task = activeTasks.find((t) => t.correlationId === generation.correlationId);
      const setId = task?.navigation?.params?.setId;
      return typeof setId === "string" && setId ? [{ generation, setId }] : [];
    });
    if (resolved.length === 0) return;

    for (const { generation, setId } of resolved) {
      bindRealSetId(generation, setId);
    }
    setPendingGenerations((prev) =>
      prev.filter(
        (generation) =>
          !resolved.some((entry) => entry.generation.correlationId === generation.correlationId),
      ),
    );
  }, [pendingGenerations, activeTasks, bindRealSetId]);

  // The task record is the only end-of-run signal: it disappears on cancel and on
  // completion alike. Disappearance only counts once the task has been seen at
  // least once, otherwise the gap before the first discovery poll looks like one.
  React.useEffect(() => {
    if (pendingGenerations.length === 0) return;
    const liveCorrelationIds = new Set(activeTasks.map((task) => task.correlationId));

    const ended = pendingGenerations.filter(
      (generation) => generation.taskSeen && !liveCorrelationIds.has(generation.correlationId),
    );
    const newlySeen = pendingGenerations.filter(
      (generation) => !generation.taskSeen && liveCorrelationIds.has(generation.correlationId),
    );
    if (ended.length === 0 && newlySeen.length === 0) return;

    // A placeholder still standing here will never be filled — its run ended
    // without ever publishing a set id, so the card represents nothing.
    for (const generation of ended) {
      removeOptimisticSet(generation.placeholderId);
    }

    setPendingGenerations((prev) =>
      prev
        .filter(
          (generation) =>
            !ended.some((entry) => entry.correlationId === generation.correlationId),
        )
        .map((generation) =>
          newlySeen.some((entry) => entry.correlationId === generation.correlationId)
            ? { ...generation, taskSeen: true }
            : generation,
        ),
    );
    // Leaving a workspace whose set was cancelled is the "selected set vanished
    // from the list" effect's job — a completed run also lands here, and it must
    // not eject the user from the set they were waiting for.
  }, [pendingGenerations, activeTasks, removeOptimisticSet]);

  const generateSet = useGeneratePersonaOpportunitySet();

  const deleteSet = useDeletePersonaOpportunitySet();

  const retrySet = useRetryPersonaOpportunitySet();

  const regenerateSet = useRegeneratePersonaOpportunitySet();

  // After a cancel removes the set from cache, leave the workspace immediately — do not
  // wait on detail reads; stale detail must not keep a soft-deleted set open.
  React.useEffect(() => {
    const setId = search.setId;
    if (!setId || setsLoading) return;
    if (sets.some((set) => set.id === setId)) return;
    returnToSetsOverview();
  }, [search.setId, sets, setsLoading, returnToSetsOverview]);

  const handleTabChange = React.useCallback((tab: PersonaOpportunityTabKey) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        tab,
      }),
    });
  }, [navigate]);

  const handleViewChange = React.useCallback((view: PersonaOpportunityViewMode) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        view,
      }),
    });
  }, [navigate]);

  const handleSearchChange = React.useCallback(
    (q: string) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          q: q.trim() ? q : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleStatusFilterChange = React.useCallback(
    (status: PersonaOpportunityStatusFilter) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          status,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSetChange = React.useCallback((set: PersonaOpportunitySet | null) => {
    if (!set) {
      // Back to the grid is a deliberate choice to stay there: no in-flight run
      // may yank the user into a workspace when it finishes.
      setPendingGenerations((prev) =>
        prev.some((generation) => generation.autoOpen)
          ? prev.map((generation) => ({ ...generation, autoOpen: false }))
          : prev,
      );
    }
    void navigate({
      search: (previous) => ({
        ...previous,
        setId: set?.id,
        tab: set ? previous.tab : defaultSearch.tab,
        view: set ? previous.view : defaultSearch.view,
      }),
    });
  }, [navigate]);

  const handleStopSet = React.useCallback((set: PersonaOpportunitySet) => {
    if (isPendingPersonaSetId(set.id)) {
      // No server row yet, so the correlation is the only handle on the job —
      // and it must be this card's own run, not whichever started most recently.
      const generation = pendingGenerations.find(
        (candidate) => candidate.placeholderId === set.id,
      );
      if (generation) {
        void cancelTask(generation.correlationId);
        void dismissTask(generation.correlationId).catch(() => {});
        untrackPendingGeneration(generation.correlationId);
      }
      removeOptimisticSet(set.id);
    } else if (!stopPersonaOpportunityGeneration(set.id)) {
      // Nothing was cancelled: no cancellable task matched, so the worker is
      // still running. Clearing the card and navigating away would tell the user
      // it stopped, then hand them the same GENERATING card on the next poll.
      toast.error(
        t(
          "personaOpportunities.toast.stopFailed",
          "Couldn't stop this generation — it may have already finished.",
        ),
      );
      return;
    }

    queryClient.removeQueries({
      queryKey: personaOpportunitySetKeys.detail(
        normalizedPersonaOpportunitySetTenantId(activeTenantId),
        set.id,
      ),
    });
    if (
      search.setId === set.id ||
      selectedSet?.status === "GENERATING" ||
      set.status === "GENERATING"
    ) {
      returnToSetsOverview();
    }
  }, [
    activeTenantId,
    pendingGenerations,
    queryClient,
    removeOptimisticSet,
    returnToSetsOverview,
    search.setId,
    selectedSet?.status,
    stopPersonaOpportunityGeneration,
    t,
    untrackPendingGeneration,
  ]);

  const handleGenerateSet = React.useCallback((values: CreatePersonaSetFormValues) => {
    if (!activeTenantId) return;

    const placeholderId = createPendingPersonaSetId();
    queryClient.setQueryData<PersonaOpportunitySetListPage>(
      listQueryKey,
      (prev) =>
        applyOptimisticCreateToList(
          prev,
          createOptimisticPersonaSetRow(values, activeTenantId, placeholderId),
        ),
    );

    generateSet.mutate(
      buildGeneratePersonaOpportunitySetRequest(values, {
        tenantId: activeTenantId,
        idempotencyKey: crypto.randomUUID(),
        canUseQueryTuning,
      }),
      {
        onSuccess: ({ correlationId }) =>
          trackPendingGeneration(correlationId, placeholderId, true),
        onError: () => removeOptimisticSet(placeholderId),
      },
    );
  }, [
    activeTenantId,
    canUseQueryTuning,
    generateSet,
    listQueryKey,
    queryClient,
    removeOptimisticSet,
    trackPendingGeneration,
  ]);

  const handleDeleteSet = React.useCallback(
    (set: PersonaOpportunitySet) => {
      deleteSet.mutate(set.id, {
        onSuccess: () => {
          if (search.setId === set.id) {
            returnToSetsOverview();
          }
        },
      });
    },
    [deleteSet, returnToSetsOverview, search.setId],
  );

  const handleRetrySet = React.useCallback(
    (set: PersonaOpportunitySet) => {
      if (!activeTenantId) return;
      markSetGeneratingOptimistically(set.id);
      // The set already exists, so there is nothing to wait for before showing it.
      openSet(set.id, { replace: true });
      retrySet.mutate(
        {
          setId: set.id,
          body: {
            tenantId: activeTenantId,
            idempotencyKey: crypto.randomUUID(),
          },
        },
        {
          onError: () => {
            void queryClient.invalidateQueries({ queryKey: listQueryKey });
            void queryClient.invalidateQueries({
              queryKey: personaOpportunitySetKeys.detail(
                normalizedPersonaOpportunitySetTenantId(activeTenantId),
                set.id,
              ),
            });
          },
        },
      );
    },
    [
      activeTenantId,
      listQueryKey,
      markSetGeneratingOptimistically,
      openSet,
      queryClient,
      retrySet,
    ],
  );

  /** The failure toast only carries a set id, not the row it came from. */
  const handleRetrySetById = React.useCallback(
    (setId: string) => {
      const set = sets.find((candidate) => candidate.id === setId);
      if (set) handleRetrySet(set);
    },
    [handleRetrySet, sets],
  );

  const handleRegenerateSet = React.useCallback(
    (input: {
      set: PersonaOpportunitySet;
      mode: "overwrite" | "new_set";
      name?: string;
    }) => {
      if (!activeTenantId) return;
      // Overwrite reuses the set id, so only new_set needs a placeholder.
      let placeholderId: string | null = null;

      if (input.mode === "overwrite") {
        markSetGeneratingOptimistically(input.set.id);
      } else {
        const newSetPlaceholderId = createPendingPersonaSetId();
        placeholderId = newSetPlaceholderId;
        queryClient.setQueryData<PersonaOpportunitySetListPage>(
          listQueryKey,
          (prev) =>
            applyOptimisticCreateToList(
              prev,
              createOptimisticGeneratingSetRow({
                id: newSetPlaceholderId,
                tenantId: activeTenantId,
                name: input.name?.trim() || input.set.name,
                industry: input.set.industry,
                industryCompetitors: input.set.industryCompetitors,
                adPlatform: input.set.adPlatform,
              }),
            ),
        );
        // Leave the source workspace so the new GENERATING card is visible on the grid.
        returnToSetsOverview();
      }

      regenerateSet.mutate(
        {
          setId: input.set.id,
          body: {
            tenantId: activeTenantId,
            idempotencyKey: crypto.randomUUID(),
            mode: input.mode,
            ...(input.mode === "new_set" && input.name ? { name: input.name } : {}),
          },
        },
        {
          onSuccess: ({ correlationId }) => {
            if (placeholderId) trackPendingGeneration(correlationId, placeholderId, true);
          },
          onError: () => {
            if (input.mode === "overwrite") {
              void queryClient.invalidateQueries({ queryKey: listQueryKey });
              void queryClient.invalidateQueries({
                queryKey: personaOpportunitySetKeys.detail(
                  normalizedPersonaOpportunitySetTenantId(activeTenantId),
                  input.set.id,
                ),
              });
            } else if (placeholderId) {
              removeOptimisticSet(placeholderId);
            }
          },
        },
      );
    },
    [
      activeTenantId,
      listQueryKey,
      markSetGeneratingOptimistically,
      queryClient,
      regenerateSet,
      removeOptimisticSet,
      returnToSetsOverview,
      trackPendingGeneration,
    ],
  );

  usePersonaSetGenerationToasts({
    sets,
    activeTasks,
    tenantId: activeTenantId,
    onOpenSet: openSet,
    onRetrySet: canCreateSet ? handleRetrySetById : undefined,
  });

  if (isLoading || (canListSets && setsLoading)) {
    return <PersonaOpportunitiesLoadingState />;
  }

  // A failed list read must not fall through to the empty state — that would invite
  // the user to create a set they may already have.
  if (error || setsError) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  if (!activeTenantId) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <PersonaOpportunitiesPage
      tenantId={activeTenantId}
      sets={sets}
      selectedSet={selectedSet}
      detail={detail?.set.id === search.setId ? detail : undefined}
      isDetailLoading={Boolean(search.setId) && (isDetailLoading || isDetailFetching) && !detail}
      canListSets={canListSets}
      canCreateSet={canCreateSet}
      canUseQueryTuning={canUseQueryTuning}
      // Only the in-flight POST, not "a set is generating" — concurrent
      // generations are allowed, so a running set must not block a new one.
      isCreatingSet={generateSet.isPending}
      isDeletingSet={deleteSet.isPending}
      isRegeneratingSet={regenerateSet.isPending}
      openCreateSetOnMount={search.createSet === true && !search.setId}
      onCreateSetDialogOpened={() => {
        if (!search.createSet) return;
        void navigate({
          search: (previous) => ({ ...previous, createSet: undefined }),
          replace: true,
        });
      }}
      onSetChange={handleSetChange}
      onGenerateSet={canCreateSet ? handleGenerateSet : undefined}
      onStopSet={canCreateSet ? handleStopSet : undefined}
      onDeleteSet={canCreateSet ? handleDeleteSet : undefined}
      onRetrySet={canCreateSet ? handleRetrySet : undefined}
      onRegenerateSet={canCreateSet ? handleRegenerateSet : undefined}
      activeTab={search.tab}
      onTabChange={handleTabChange}
      view={search.view}
      onViewChange={handleViewChange}
      search={search.q ?? ""}
      onSearchChange={handleSearchChange}
      statusFilter={search.status ?? "all"}
      onStatusFilterChange={handleStatusFilterChange}
    />
  );
}

export const Route = createFileRoute("/_authenticated/persona-opportunities")({
  beforeLoad: () => assertPagePermission("persona-opportunities"),
  validateSearch: personaOpportunitiesSearchSchema,
  loaderDeps: ({ search }) => ({ setId: search.setId }),
  loader: ({ context, deps }) => {
    const tenantId = useAuth.getState().activeTenantId;
    if (tenantId) {
      // Fire-and-forget: the component owns loading and error UI, and a viewer
      // without the list grant would only collect a 403 here.
      void context.queryClient.prefetchQuery(adPlatformsQueryOptions(tenantId));
      void context.queryClient.prefetchQuery(personaOpportunitySetsQueryOptions(tenantId));
      if (deps.setId) {
        void context.queryClient.prefetchQuery(
          personaOpportunitySetDetailQueryOptions(tenantId, deps.setId),
        );
      }
    }
    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("persona-opportunities"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
  },
  component: PersonaOpportunitiesRoute,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});
